import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, seedAPI, propertyAPI, storeAPI, purchaseOrderAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Building2, 
  Store, 
  Package, 
  Truck, 
  Users, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  ArrowLeftRight,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Loader2,
  LayoutGrid,
  Info,
  PackageX,
  UserX,
  Clock,
  Activity,
  IndianRupee,
  Calendar,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { formatNumber, formatDate, formatCurrency } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['hsl(30, 70%, 50%)', 'hsl(160, 50%, 40%)', 'hsl(200, 50%, 45%)', 'hsl(45, 70%, 55%)', 'hsl(15, 70%, 55%)', 'hsl(270, 50%, 50%)', 'hsl(120, 40%, 45%)'];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [paymentReminders, setPaymentReminders] = useState(null);
  
  // Filters
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedStore, setSelectedStore] = useState('all');
  const [filteredStores, setFilteredStores] = useState([]);

  const fetchMasterData = async () => {
    try {
      const [propertiesRes, storesRes] = await Promise.all([
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' })
      ]);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setFilteredStores(storesRes.data);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const fetchPaymentReminders = async () => {
    try {
      const response = await purchaseOrderAPI.getPaymentReminders();
      setPaymentReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch payment reminders:', error);
    }
  };

  const fetchDashboard = async (propertyId = null, storeId = null) => {
    try {
      setLoading(true);
      const params = {};
      if (propertyId && propertyId !== 'all') params.property_id = propertyId;
      if (storeId && storeId !== 'all') params.store_id = storeId;
      const response = await dashboardAPI.get(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 401) {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const response = await seedAPI.seed();
      toast.success('Initial data seeded successfully!', {
        description: `Admin: ${response.data.admin_email} / ${response.data.admin_password}`
      });
      fetchMasterData();
      fetchDashboard();
    } catch (error) {
      if (error.response?.data?.message === 'Data already seeded') {
        toast.info('Data already seeded');
      } else {
        toast.error('Failed to seed data');
      }
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    fetchDashboard();
    fetchPaymentReminders();
  }, []);

  // Filter stores when property changes
  useEffect(() => {
    if (selectedProperty === 'all') {
      setFilteredStores(stores);
    } else {
      setFilteredStores(stores.filter(s => s.property_id === selectedProperty));
    }
    setSelectedStore('all');
  }, [selectedProperty, stores]);

  const handleApplyFilters = () => {
    fetchDashboard(selectedProperty, selectedStore);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  const counts = data?.counts || {};
  const stockMovement = data?.stock_movement || {};
  const lowStockItems = data?.low_stock_items || [];
  const topMovedItems = data?.top_moved_items || [];
  const recentTransactions = data?.recent_transactions || [];
  const storeSummary = data?.store_summary || [];
  const propertySummary = data?.property_summary || [];
  const categoryDistribution = data?.category_distribution || [];
  const exceptions = data?.exceptions || {};
  const transactions = data?.transactions || {};
  const userInfo = data?.user || {};

  // Prepare chart data
  const stockMovementChartData = [
    { name: 'Today', inward: stockMovement.inward?.today || 0, issue: stockMovement.issue?.today || 0, transfer: stockMovement.transfer?.today || 0 },
    { name: 'This Week', inward: stockMovement.inward?.week || 0, issue: stockMovement.issue?.week || 0, transfer: stockMovement.transfer?.week || 0 },
    { name: 'This Month', inward: stockMovement.inward?.month || 0, issue: stockMovement.issue?.month || 0, transfer: stockMovement.transfer?.month || 0 },
  ];

  const topItemsChartData = topMovedItems.slice(0, 7).map((item, index) => ({
    name: item.item || 'Unknown',
    value: item.quantity || 0,
    fill: COLORS[index % COLORS.length]
  }));

  const categoryChartData = categoryDistribution.slice(0, 8).map((cat, index) => ({
    name: cat.category_name || 'Uncategorized',
    value: cat.total_quantity || 0,
    items: cat.item_count || 0,
    fill: COLORS[index % COLORS.length]
  }));

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'Inward': return <ArrowDownToLine className="h-4 w-4 text-green-600" />;
      case 'Issue': return <ArrowUpFromLine className="h-4 w-4 text-red-600" />;
      case 'Transfer': return <ArrowLeftRight className="h-4 w-4 text-blue-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTransactionBg = (type) => {
    switch (type) {
      case 'Inward': return 'bg-green-50 border-green-100';
      case 'Issue': return 'bg-red-50 border-red-100';
      case 'Transfer': return 'bg-blue-50 border-blue-100';
      default: return 'bg-gray-50 border-gray-100';
    }
  };

  const getTransactionBadge = (type) => {
    switch (type) {
      case 'Inward': return 'bg-green-100 text-green-700';
      case 'Issue': return 'bg-red-100 text-red-700';
      case 'Transfer': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {userInfo.role_name && <span>Logged in as <span className="font-medium">{userInfo.role_name}</span> • </span>}
              Overview of your inventory operations
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => fetchDashboard(selectedProperty, selectedStore)} disabled={loading} data-testid="refresh-dashboard-btn" className="h-9">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            <Button onClick={handleSeed} disabled={seeding} variant="secondary" size="sm" data-testid="seed-data-btn" className="h-9">
              {seeding ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Package className="h-4 w-4 mr-2" />
              )}
              Initialize Data
            </Button>
          </div>
        </div>

        {/* Filters Row */}
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Property</label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[180px]" data-testid="filter-property">
                  <SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Store</label>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger className="w-[180px]" data-testid="filter-store">
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {filteredStores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyFilters} data-testid="apply-dashboard-filters">
              Apply Filters
            </Button>
          </div>
        </Card>
      </div>

      {/* Summary Cards Row 1 - Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="card-hover" data-testid="stat-properties">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-100">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Properties</p>
                <p className="text-xl font-bold">{formatNumber(counts.properties)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-stores">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-green-100">
                <Store className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Stores</p>
                <p className="text-xl font-bold">{formatNumber(counts.stores)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-items">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-100">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Items</p>
                <p className="text-xl font-bold">{formatNumber(counts.items)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-vendors">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-orange-100">
                <Truck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Vendors</p>
                <p className="text-xl font-bold">{formatNumber(counts.vendors)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-users">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-pink-100">
                <Users className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Users</p>
                <p className="text-xl font-bold">{formatNumber(counts.users)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-categories">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-100">
                <LayoutGrid className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Categories</p>
                <p className="text-xl font-bold">{formatNumber(counts.categories)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards Row 2 - Stock & Transactions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-hover bg-gradient-to-br from-teal-50 to-white" data-testid="stat-total-stock">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Stock Qty</p>
                <p className="text-2xl font-bold text-teal-700">{formatNumber(counts.total_stock_quantity)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-teal-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-emerald-50 to-white" data-testid="stat-stock-value">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Stock Value</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(counts.total_stock_value || 0)}</p>
              </div>
              <IndianRupee className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-amber-50 to-white" data-testid="stat-low-stock">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Low Stock Alerts</p>
                <p className="text-2xl font-bold text-amber-700">{formatNumber(counts.low_stock_count)}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-indigo-50 to-white" data-testid="stat-today-transactions">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Today's Transactions</p>
                <p className="text-2xl font-bold text-indigo-700">{formatNumber(transactions.today)}</p>
              </div>
              <Activity className="h-8 w-8 text-indigo-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-violet-50 to-white" data-testid="stat-month-transactions">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-violet-700">{formatNumber(transactions.month)}</p>
              </div>
              <Clock className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Reminders Widget */}
      {paymentReminders && (paymentReminders.summary?.total_overdue > 0 || paymentReminders.summary?.total_due_this_week > 0) && (
        <Card className="border-l-4 border-l-amber-500" data-testid="payment-reminders-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-lg">Payment Reminders</CardTitle>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/purchase-orders')}
                className="text-muted-foreground hover:text-foreground"
              >
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <CardDescription>Vendor payments that need attention</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className={`p-3 rounded-lg ${paymentReminders.summary?.total_overdue > 0 ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle className={`h-4 w-4 ${paymentReminders.summary?.total_overdue > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  <span className="text-xs text-muted-foreground">Overdue</span>
                </div>
                <p className={`text-xl font-bold ${paymentReminders.summary?.total_overdue > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                  {paymentReminders.summary?.total_overdue || 0}
                </p>
                {paymentReminders.summary?.total_overdue_amount > 0 && (
                  <p className="text-xs text-red-600">{formatCurrency(paymentReminders.summary.total_overdue_amount)}</p>
                )}
              </div>
              
              <div className={`p-3 rounded-lg ${paymentReminders.summary?.total_due_this_week > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <Calendar className={`h-4 w-4 ${paymentReminders.summary?.total_due_this_week > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                  <span className="text-xs text-muted-foreground">Due This Week</span>
                </div>
                <p className={`text-xl font-bold ${paymentReminders.summary?.total_due_this_week > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                  {paymentReminders.summary?.total_due_this_week || 0}
                </p>
                {paymentReminders.summary?.total_due_this_week_amount > 0 && (
                  <p className="text-xs text-amber-600">{formatCurrency(paymentReminders.summary.total_due_this_week_amount)}</p>
                )}
              </div>
            </div>

            {/* Overdue Payments List */}
            {paymentReminders.overdue?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" /> Overdue Payments
                </h4>
                <div className="space-y-2">
                  {paymentReminders.overdue.slice(0, 3).map((po) => (
                    <div 
                      key={po.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100 hover:bg-red-100 cursor-pointer transition-colors"
                      onClick={() => navigate('/purchase-orders')}
                    >
                      <div>
                        <p className="font-medium text-sm">{po.po_number}</p>
                        <p className="text-xs text-muted-foreground">{po.vendor_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-700">{formatCurrency(po.balance_due)}</p>
                        <p className="text-xs text-red-600">{Math.abs(po.days_until_due)} days overdue</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Due This Week List */}
            {paymentReminders.due_this_week?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-1">
                  <Calendar className="h-4 w-4" /> Due This Week
                </h4>
                <div className="space-y-2">
                  {paymentReminders.due_this_week.slice(0, 3).map((po) => (
                    <div 
                      key={po.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100 hover:bg-amber-100 cursor-pointer transition-colors"
                      onClick={() => navigate('/purchase-orders')}
                    >
                      <div>
                        <p className="font-medium text-sm">{po.po_number}</p>
                        <p className="text-xs text-muted-foreground">{po.vendor_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-amber-700">{formatCurrency(po.balance_due)}</p>
                        <p className="text-xs text-amber-600">
                          {po.days_until_due === 0 ? 'Due today' : `${po.days_until_due} days left`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Movement Chart */}
        <Card data-testid="stock-movement-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Stock Movement</CardTitle>
            <CardDescription>Inward, Issue & Transfer transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockMovementChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="inward" name="Inward" fill="hsl(142, 76%, 36%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="issue" name="Issue" fill="hsl(0, 84%, 60%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="transfer" name="Transfer" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution Chart */}
        <Card data-testid="category-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Category Distribution</CardTitle>
            <CardDescription>Stock quantity by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 md:h-64">
              {categoryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="45%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={false}
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [formatNumber(value), 'Quantity']}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend 
                      layout="horizontal"
                      align="center"
                      verticalAlign="bottom"
                      wrapperStyle={{ paddingTop: '16px' }}
                      formatter={(value, entry) => (
                        <span className="text-xs text-muted-foreground">{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No category data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store & Property Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Summary */}
        <Card data-testid="store-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="h-5 w-5 text-green-600" />
              Store-wise Stock
            </CardTitle>
          </CardHeader>
          <CardContent>
            {storeSummary.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {storeSummary.map((store, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div>
                      <p className="font-medium text-sm">{store.store_name}</p>
                      <p className="text-xs text-muted-foreground">{store.item_count || 0} items</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatNumber(store.total_quantity)}</p>
                      {store.low_stock_count > 0 && (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> {store.low_stock_count} low
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Store className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No store data</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Moved Items */}
        <Card data-testid="top-items-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top Issued Items</CardTitle>
            <CardDescription>Most frequently issued items (last 30 days)</CardDescription>
          </CardHeader>
          <CardContent>
            {topItemsChartData.length > 0 ? (
              <div className="space-y-2">
                {topItemsChartData.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.fill }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm">{formatNumber(item.value)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Package className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No issue data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alerts */}
        <Card data-testid="low-stock-alerts">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Low Stock Alerts
              {lowStockItems.length > 0 && (
                <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {lowStockItems.length} items
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lowStockItems.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {lowStockItems.slice(0, 10).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{item.item_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.store_name} • {item.category_name || 'No category'}
                      </p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="font-semibold text-amber-700">{formatNumber(item.quantity)}</p>
                      <p className="text-xs text-muted-foreground">Min: {formatNumber(item.min_stock_level)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Package className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No low stock alerts</p>
                <p className="text-xs text-muted-foreground mt-1">All items are above minimum levels</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card data-testid="recent-transactions">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Recent Transactions</CardTitle>
            <CardDescription>Latest stock movements</CardDescription>
          </CardHeader>
          <CardContent>
            {recentTransactions.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {recentTransactions.slice(0, 8).map((txn, index) => (
                  <div key={index} className={`flex items-center gap-3 p-3 rounded-lg border ${getTransactionBg(txn.type)}`}>
                    <div className="p-1.5 rounded-lg bg-white/80">
                      {getTransactionIcon(txn.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{txn.transaction_number}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {txn.store_name} • {formatDate(txn.date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getTransactionBadge(txn.type)}`}>
                        {txn.type}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{txn.item_count} items</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Activity className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No recent transactions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exceptions & Data Quality Alerts */}
      {(exceptions.items_no_min_stock > 0 || exceptions.items_no_vendor > 0 || exceptions.unused_items > 0) && (
        <Card data-testid="exceptions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Data Quality Alerts
            </CardTitle>
            <CardDescription>Items that may need attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {exceptions.items_no_min_stock > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-100">
                  <PackageX className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium">{exceptions.items_no_min_stock} items</p>
                    <p className="text-xs text-muted-foreground">Missing min stock level</p>
                  </div>
                </div>
              )}
              {exceptions.items_no_vendor > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-100">
                  <UserX className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium">{exceptions.items_no_vendor} items</p>
                    <p className="text-xs text-muted-foreground">No vendor assigned</p>
                  </div>
                </div>
              )}
              {exceptions.unused_items > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <Package className="h-5 w-5 text-slate-600" />
                  <div>
                    <p className="text-sm font-medium">{exceptions.unused_items} items</p>
                    <p className="text-xs text-muted-foreground">Created but never used</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
